import { FC, useEffect, useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";

import { FieldBaseProps } from "../FieldBaseProps";
import { useInputDisabled } from "../InputGroup/useInputDisabled";
import { useInputReadonly } from "../InputGroup/useInputReadonly";
import { SubmitGroup } from "../SubmitGroup";
import { ValidityGroup } from "../ValidityGroup";
import { DisabledRequiredRule, required as requiredRule } from "../validation/rules";
import { Input } from "./Input";
import { SharedProps } from "./SharedProps";

/**
 * The `Field` component properties.
 */

export interface FieldProps extends FieldBaseProps<string | null>, SharedProps {
  /**
   * The unique identifier for the CodeArea component. This is used to associate the label and instructions with the textarea for accessibility purposes.
   */
  readonly id?: string;
}

/**
 * A React Hook Form compatible TextArea Field component. For more information, see the documentation for
 * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
 */
export const Field: FC<FieldProps> = (props) => {
  const { id, name, required = false, defaultValue = null, validator, ...restProps } = props;

  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);

  const rules = useMemo(
    () => ({
      ...(disabled || readOnly ? DisabledRequiredRule : requiredRule(required)),
      validate: disabled || readOnly ? undefined : validator,
    }),
    [disabled, readOnly, required, validator],
  );

  const { resetField } = useFormContext();

  useEffect(() => {
    if (disabled) {
      resetField(name);
    }
  }, [disabled]);

  const { field, fieldState, formState } = useController({
    name,
    defaultValue,
    rules,
  });

  const { ref, value, onChange, onBlur } = field;
  const { invalid, error } = fieldState;
  const { isSubmitting } = formState;

  return (
    <SubmitGroup submitting={isSubmitting}>
      <ValidityGroup invalid={invalid} error={error?.message}>
        <Input
          {...restProps}
          ref={ref}
          id={id}
          value={value}
          required={required}
          defaultValue={defaultValue}
          onChange={onChange}
          onFocusExit={onBlur}
          error={error?.message}
        />
      </ValidityGroup>
    </SubmitGroup>
  );
};
